import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { Friend, FriendRequest, InviteCode } from "../../types";
import { ConfirmationDialog } from "../Activities/ConfirmationDialog";
import { FriendListItem } from "./FriendListItem";
import { FriendRequestItem } from "./FriendRequestItem";
import { friendsService } from "../../services/friendsService";
import { authService } from "../../services/authService";
import { useAuth } from "../../hooks/useAuth";
import { getUserInitial } from "../../utils/formatting";
import debugx from "debugx";

const debug = debugx("bikedash:friends");

interface ConfirmationState {
  visible: boolean;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  onConfirm: () => void;
  onCancel?: () => void;
  isDangerous?: boolean;
  cancelIsPrimary?: boolean;
}

interface FriendsModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialTab?: "friends" | "invite" | "profile";
}

// Helper to generate 6-digit invite code
const generateInviteCode = (): string => {
  return Math.floor(100000 + Math.random() * 900000).toString();
};

// Helper to format time remaining
const formatTimeRemaining = (expiresAt: number): string => {
  const now = Date.now();
  const remaining = expiresAt - now;

  if (remaining <= 0) return "Expired";

  const hours = Math.floor(remaining / (1000 * 60 * 60));
  const minutes = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60));

  if (hours > 0) {
    return `${hours}h ${minutes}m remaining`;
  }
  return `${minutes}m remaining`;
};

export function FriendsModal({
  isOpen,
  onClose,
  initialTab = "friends",
}: FriendsModalProps) {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<"friends" | "invite" | "profile">(
    initialTab,
  );
  const [friends, setFriends] = useState<Friend[]>([]);
  const [friendRequests, setFriendRequests] = useState<FriendRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const [emailInput, setEmailInput] = useState("");
  const [codeInput, setCodeInput] = useState("");
  const [myInviteCode, setMyInviteCode] = useState<InviteCode | null>(null);
  const [autoJoinTriggered, setAutoJoinTriggered] = useState(false);
  const [, forceUpdate] = useState({});

  // Profile settings
  const [username, setUsername] = useState("");
  const [mapIcon, setMapIcon] = useState<string>("");
  const [mapColor, setMapColor] = useState<string>("#6366f1");
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const initialProfileRef = useRef({ username: "", mapIcon: "", mapColor: "" });
  const prevActiveTabRef = useRef(activeTab);

  const [confirmation, setConfirmation] = useState<ConfirmationState>({
    visible: false,
    title: "",
    message: "",
    onConfirm: () => {},
  });

  // Update active tab when initialTab changes
  useEffect(() => {
    if (isOpen) {
      setActiveTab(initialTab);
      // Dismiss new requests notification when opening friends modal
      friendsService.dismissNewRequests();
    }
  }, [isOpen, initialTab]);

  // Load user profile data only when modal opens
  useEffect(() => {
    if (isOpen && user) {
      const newProfile = {
        username: user.username,
        mapIcon: user.mapIcon || "",
        mapColor: user.mapColor || "#6366f1",
      };
      setUsername(newProfile.username);
      setMapIcon(newProfile.mapIcon);
      setMapColor(newProfile.mapColor);
      initialProfileRef.current = newProfile;
    }
  }, [isOpen]);

  // Load friends and requests
  useEffect(() => {
    if (!isOpen) return;

    const loadData = async () => {
      setLoading(true);
      try {
        const [friendsData, requestsData] = await Promise.all([
          friendsService.getFriends(),
          friendsService.getFriendRequests(),
        ]);

        setFriends(friendsData);
        setFriendRequests(requestsData);
        debug(
          "Loaded %d friends and %d requests",
          friendsData.length,
          requestsData.length,
        );
      } catch (error) {
        debug.error("Failed to load friends data:", error);
      } finally {
        setLoading(false);
      }
    };

    loadData();

    // Start modal refresh (5s interval)
    friendsService.startModalRefresh();

    // Subscribe to changes
    const unsubscribeFriends = friendsService.onFriendsChange(setFriends);
    const unsubscribeRequests =
      friendsService.onRequestsChange(setFriendRequests);

    return () => {
      // Stop modal refresh when closing
      friendsService.stopModalRefresh();
      unsubscribeFriends();
      unsubscribeRequests();
    };
  }, [isOpen]);

  // Load invite code when opening modal
  useEffect(() => {
    if (!isOpen) return;

    const loadInviteCode = async () => {
      try {
        const code = await friendsService.getMyInviteCode();
        setMyInviteCode(code);
        debug("Loaded invite code:", code?.code || "none");
      } catch (error) {
        debug.error("Failed to load invite code:", error);
      }
    };

    loadInviteCode();
  }, [isOpen]);

  // Update invite code expiry countdown
  useEffect(() => {
    if (!myInviteCode || activeTab !== "invite") return;

    const interval = setInterval(() => {
      forceUpdate({});
    }, 60000); // Update every minute

    return () => clearInterval(interval);
  }, [myInviteCode, activeTab]);

  // Auto-save profile changes with 2s debounce (only when on profile tab)
  useEffect(() => {
    if (!user || activeTab !== "profile") return;

    // Don't auto-save on initial load
    if (
      username === initialProfileRef.current.username &&
      mapIcon === initialProfileRef.current.mapIcon &&
      mapColor === initialProfileRef.current.mapColor
    ) {
      return;
    }

    // Clear existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // Set new timeout
    saveTimeoutRef.current = setTimeout(async () => {
      try {
        await authService.updateProfile({
          username,
          mapIcon: mapIcon !== undefined ? mapIcon : undefined,
          mapColor: mapColor || undefined,
        });
        initialProfileRef.current = { username, mapIcon, mapColor };
        debug("Auto-saved profile:", { username, mapIcon, mapColor });
      } catch (error) {
        debug.error("Failed to auto-save profile:", error);
      }
    }, 2000);

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [username, mapIcon, mapColor, user, activeTab]);

  // Save immediately when leaving profile tab or closing modal
  useEffect(() => {
    const prevTab = prevActiveTabRef.current;
    prevActiveTabRef.current = activeTab;

    // Save if we just left the profile tab
    if (prevTab === "profile" && activeTab !== "profile" && user) {
      const hasChanges =
        username !== initialProfileRef.current.username ||
        mapIcon !== initialProfileRef.current.mapIcon ||
        mapColor !== initialProfileRef.current.mapColor;

      if (hasChanges) {
        if (saveTimeoutRef.current) {
          clearTimeout(saveTimeoutRef.current);
        }

        authService
          .updateProfile({
            username,
            mapIcon: mapIcon !== undefined ? mapIcon : undefined,
            mapColor: mapColor || undefined,
          })
          .then(() => {
            initialProfileRef.current = { username, mapIcon, mapColor };
            debug("Saved profile on tab switch:", {
              username,
              mapIcon,
              mapColor,
            });
          })
          .catch((error) => {
            debug.error("Failed to save profile on tab switch:", error);
          });
      }
    }
  }, [activeTab, username, mapIcon, mapColor, user]);

  // Save when closing modal
  useEffect(() => {
    if (!isOpen && user && prevActiveTabRef.current === "profile") {
      const hasChanges =
        username !== initialProfileRef.current.username ||
        mapIcon !== initialProfileRef.current.mapIcon ||
        mapColor !== initialProfileRef.current.mapColor;

      if (hasChanges) {
        if (saveTimeoutRef.current) {
          clearTimeout(saveTimeoutRef.current);
        }

        authService
          .updateProfile({
            username,
            mapIcon: mapIcon !== undefined ? mapIcon : undefined,
            mapColor: mapColor || undefined,
          })
          .then(() => {
            initialProfileRef.current = { username, mapIcon, mapColor };
            debug("Saved profile on modal close:", {
              username,
              mapIcon,
              mapColor,
            });
          })
          .catch((error) => {
            debug.error("Failed to save profile on modal close:", error);
          });
      }
    }
  }, [isOpen, username, mapIcon, mapColor, user]);

  const handleRemoveFriend = useCallback(
    async (friend: Friend) => {
      setConfirmation({
        visible: true,
        title: "Remove Friend",
        message: `Are you sure you want to remove ${friend.username} from your friends?`,
        confirmText: "Remove",
        cancelText: "Cancel",
        isDangerous: true,
        onConfirm: async () => {
          setConfirmation({ ...confirmation, visible: false });
          try {
            await friendsService.removeFriend(friend.id);
            debug("Removed friend: %s", friend.username);
            // Refresh friends list immediately
            const friendsData = await friendsService.getFriends();
            setFriends(friendsData);
          } catch (error) {
            debug.error("Failed to remove friend:", error);
          }
        },
      });
    },
    [confirmation],
  );

  const handleAcceptRequest = useCallback(
    async (
      request: FriendRequest,
      recipientSettings: {
        shareActivities: boolean;
        shareLocation: boolean;
        shareStatus: boolean;
        shareRoutes: boolean;
      },
    ) => {
      try {
        await friendsService.acceptFriendRequest(request.id, recipientSettings);
        debug("Accepted friend request from: %s", request.fromUsername);
        // Refresh both lists immediately
        const [friendsData, requestsData] = await Promise.all([
          friendsService.getFriends(),
          friendsService.getFriendRequests(),
        ]);
        setFriends(friendsData);
        setFriendRequests(requestsData);
        // Switch to friends tab to show the new friend
        setActiveTab("friends");
      } catch (error) {
        debug.error("Failed to accept friend request:", error);
      }
    },
    [],
  );

  const handleDeclineRequest = useCallback(
    async (request: FriendRequest) => {
      const isOutgoing = request.direction === "outgoing";
      const displayName = isOutgoing
        ? request.pendingUser
          ? request.toEmail
          : request.toUsername || request.invitedUsername || "this user"
        : request.fromUsername;

      setConfirmation({
        visible: true,
        title: isOutgoing ? "Cancel Request?" : "Decline Request?",
        message: isOutgoing
          ? `Cancel your friend request to ${displayName}?`
          : `Decline friend request from ${displayName}?`,
        confirmText: isOutgoing ? "Cancel Request" : "Decline",
        cancelText: "Keep",
        isDangerous: true,
        cancelIsPrimary: true,
        onConfirm: async () => {
          try {
            await friendsService.declineFriendRequest(request.id);
            debug("Declined friend request from: %s", request.fromUsername);
            // Refresh requests list immediately
            const requestsData = await friendsService.getFriendRequests();
            setFriendRequests(requestsData);
            setConfirmation({ ...confirmation, visible: false });
          } catch (error) {
            debug.error("Failed to decline friend request:", error);
            setConfirmation({ ...confirmation, visible: false });
          }
        },
        onCancel: () => {
          setConfirmation({ ...confirmation, visible: false });
        },
      });
    },
    [confirmation],
  );

  const handleSendEmailInvite = useCallback(async () => {
    if (!emailInput.trim()) return;

    const email = emailInput.trim();

    try {
      const result = await friendsService.sendFriendRequest({ email });
      debug("Sent friend request to: %s", email);
      setEmailInput("");

      // Refresh requests list immediately
      const requestsData = await friendsService.getFriendRequests();
      setFriendRequests(requestsData);

      // Check if this was a pending invite (user doesn't exist yet)
      const isPending = result.pending;
      const message =
        result.message || `A friend request has been sent to ${email}`;

      // If pending, stay on connect tab, otherwise switch to friends tab
      if (!isPending) {
        setActiveTab("friends");
      }

      setConfirmation({
        visible: true,
        title: isPending ? "Invite Sent" : "Friend Request Sent",
        message,
        confirmText: "OK",
        cancelText: "",
        onConfirm: () => {
          setConfirmation({ ...confirmation, visible: false });
        },
      });
    } catch (error) {
      debug.error("Failed to send friend request:", error);
      setConfirmation({
        visible: true,
        title: "Error",
        message:
          error instanceof Error ? error.message : "Failed to send request",
        confirmText: "OK",
        cancelText: "",
        onConfirm: () => {
          setConfirmation({ ...confirmation, visible: false });
        },
      });
    }
  }, [emailInput, confirmation]);

  const handleJoinWithCode = useCallback(async () => {
    if (!codeInput.trim() || codeInput.length !== 6) return;

    const code = codeInput.trim();

    try {
      // First, validate the code and get owner info
      const result = await friendsService.useInviteCode(code);

      if (!result) {
        debug.error("Failed to validate invite code");
        return;
      }

      debug("Validated invite code: %s", code);

      // Show confirmation dialog asking user to confirm
      setConfirmation({
        visible: true,
        title: "Connect with Friend",
        message: `Connect with ${result.username}?`,
        confirmText: "Connect",
        cancelText: "Cancel",
        onConfirm: async () => {
          setConfirmation({ ...confirmation, visible: false });

          try {
            // Now send the actual friend request
            await friendsService.sendFriendRequest({ inviteCode: code });
            debug("Sent friend request via code: %s", code);
            setCodeInput("");

            // Refresh requests list immediately
            const requestsData = await friendsService.getFriendRequests();
            setFriendRequests(requestsData);

            // Switch to friends tab to show the pending request
            setActiveTab("friends");
          } catch (error) {
            debug.error("Failed to send friend request:", error);
            setConfirmation({
              visible: true,
              title: "Error",
              message:
                error instanceof Error
                  ? error.message
                  : "Failed to connect with friend",
              confirmText: "OK",
              cancelText: "",
              onConfirm: () => {
                setConfirmation({ ...confirmation, visible: false });
              },
            });
          }
        },
      });
    } catch (error) {
      debug.error("Failed to validate invite code:", error);
      setConfirmation({
        visible: true,
        title: "Invalid Code",
        message:
          error instanceof Error
            ? error.message
            : "The invite code is invalid or has expired.",
        confirmText: "OK",
        cancelText: "",
        onConfirm: () => {
          setConfirmation({ ...confirmation, visible: false });
        },
      });
    }
  }, [codeInput, confirmation]);

  const handleToggleShareActivities = useCallback(async (friend: Friend) => {
    try {
      await friendsService.updateFriendSettings(friend.id, {
        shareActivities: !friend.shareActivities,
      });
      debug(
        "Toggled share activities with %s to %s",
        friend.username,
        !friend.shareActivities,
      );
    } catch (error) {
      debug.error("Failed to toggle share activities:", error);
    }
  }, []);

  const handleToggleShareLocation = useCallback(async (friend: Friend) => {
    try {
      await friendsService.updateFriendSettings(friend.id, {
        shareLocation: !friend.shareLocation,
      });
      debug(
        "Toggled share location with %s to %s",
        friend.username,
        !friend.shareLocation,
      );
    } catch (error) {
      debug.error("Failed to toggle share location:", error);
    }
  }, []);

  const handleToggleShareStatus = useCallback(async (friend: Friend) => {
    try {
      await friendsService.updateFriendSettings(friend.id, {
        shareStatus: friend.shareStatus === false ? true : false,
      });
      debug(
        "Toggled share status with %s to %s",
        friend.username,
        friend.shareStatus === false,
      );
    } catch (error) {
      debug.error("Failed to toggle share status:", error);
    }
  }, []);

  const handleToggleShareRoutes = useCallback(async (friend: Friend) => {
    try {
      await friendsService.updateFriendSettings(friend.id, {
        shareRoutes: !friend.shareRoutes,
      });
      debug(
        "Toggled share routes with %s to %s",
        friend.username,
        !friend.shareRoutes,
      );
    } catch (error) {
      debug.error("Failed to toggle share routes:", error);
    }
  }, []);

  const handleUpdateMapSettings = useCallback(
    async (friend: Friend, mapIcon: string, mapColor: string) => {
      try {
        // Empty strings mean "use default" - send as null to clear customization
        const settings: any = {
          mapIcon: mapIcon === "" ? null : mapIcon,
          mapColor: mapColor === "" ? null : mapColor,
        };

        await friendsService.updateFriendSettings(friend.id, settings);
        debug(
          "Updated map settings for %s: icon=%s color=%s",
          friend.username,
          mapIcon || "default",
          mapColor || "default",
        );
      } catch (error) {
        debug.error("Failed to update map settings:", error);
      }
    },
    [],
  );

  const handleGenerateInviteCode = useCallback(async () => {
    try {
      const newCode = await friendsService.generateInviteCode();
      if (newCode) {
        setMyInviteCode(newCode);
        debug("Generated new invite code: %s", newCode.code);
      } else {
        debug.error("Failed to generate invite code");
      }
    } catch (error) {
      debug.error("Failed to generate invite code:", error);
    }
  }, []);

  const handleShareInviteCode = useCallback(async () => {
    if (!myInviteCode) return;

    const inviteUrl = `${window.location.origin}/?invite=${myInviteCode.code}`;

    // Try Web Share API first
    if (navigator.share) {
      try {
        await navigator.share({
          title: "BikeDash Invite",
          text: "Join me on BikeDash!",
          url: inviteUrl,
        });
        debug("Shared invite link via Web Share API");
        return;
      } catch (error) {
        // User cancelled or share failed, fall back to clipboard
        debug("Web Share API failed or cancelled:", error);
      }
    }

    // Fallback to clipboard
    try {
      await navigator.clipboard.writeText(inviteUrl);
      setConfirmation({
        visible: true,
        title: "Link Copied",
        message:
          "Invite link copied! Share it with your friend to connect on BikeDash.",
        confirmText: "OK",
        cancelText: "",
        onConfirm: () => {
          setConfirmation({ ...confirmation, visible: false });
        },
      });
      debug("Copied invite link to clipboard");
    } catch (error) {
      debug.error("Failed to share invite link:", error);
    }
  }, [myInviteCode, confirmation]);

  // Handle invite code from URL parameter
  useEffect(() => {
    if (!isOpen) return;

    const urlParams = new URLSearchParams(window.location.search);
    const inviteCode = urlParams.get("invite");

    if (inviteCode && inviteCode.length === 6 && !autoJoinTriggered) {
      debug("Detected invite code from URL: %s", inviteCode);
      setCodeInput(inviteCode);
      setActiveTab("invite");
      setAutoJoinTriggered(true);

      // Clear the URL parameter
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.delete("invite");
      window.history.replaceState({}, "", newUrl.toString());
    }
  }, [isOpen, autoJoinTriggered]);

  // Auto-trigger join when code is set from URL
  useEffect(() => {
    if (autoJoinTriggered && codeInput.length === 6 && activeTab === "invite") {
      debug("Auto-triggering join with code: %s", codeInput);
      handleJoinWithCode();
      setAutoJoinTriggered(false);
    }
  }, [autoJoinTriggered, codeInput, activeTab, handleJoinWithCode]);

  // Separate incoming and outgoing requests
  const incomingRequests = useMemo(
    () =>
      friendRequests.filter(
        (r) => r.status === "pending" && r.direction === "incoming",
      ),
    [friendRequests],
  );

  const outgoingRequests = useMemo(
    () =>
      friendRequests.filter(
        (r) => r.status === "pending" && r.direction === "outgoing",
      ),
    [friendRequests],
  );

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal settings-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <button className="close-btn" onClick={onClose}>
          <span>×</span>
        </button>

        <div className="settings-tabs">
          <button
            className={`tab ${activeTab === "friends" ? "active" : ""}`}
            onClick={() => setActiveTab("friends")}
          >
            Friends ({friends.length})
            {incomingRequests.length > 0 && ` (${incomingRequests.length})`}
          </button>
          <button
            className={`tab ${activeTab === "invite" ? "active" : ""}`}
            onClick={() => setActiveTab("invite")}
          >
            Connect
          </button>
          <button
            className={`tab ${activeTab === "profile" ? "active" : ""}`}
            onClick={() => setActiveTab("profile")}
          >
            Profile
          </button>
        </div>

        <div className="settings-content">
          <div className="settings-section">
            {/* Friends Tab */}
            {activeTab === "friends" && (
              <>
                {loading ? (
                  <div
                    className="text-center text-muted"
                    style={{ padding: "40px" }}
                  >
                    Loading friends...
                  </div>
                ) : friends.length === 0 &&
                  outgoingRequests.length === 0 &&
                  incomingRequests.length === 0 ? (
                  <div
                    style={{
                      padding: "40px",
                      textAlign: "center",
                    }}
                  >
                    <div
                      style={{
                        color: "var(--text-muted)",
                        marginBottom: "16px",
                      }}
                    >
                      No friends yet
                    </div>
                    <button
                      onClick={() => setActiveTab("invite")}
                      style={{
                        padding: "10px 20px",
                        background: "var(--accent)",
                        border: "none",
                        borderRadius: "6px",
                        color: "var(--text-on-accent)",
                        cursor: "pointer",
                        fontSize: "14px",
                        fontWeight: "500",
                      }}
                    >
                      Connect with Friends
                    </button>
                  </div>
                ) : (
                  <>
                    {/* Outgoing Requests - Show First */}
                    {outgoingRequests.length > 0 && (
                      <div style={{ marginBottom: "20px" }}>
                        <h3
                          style={{
                            fontSize: "14px",
                            color: "var(--text-secondary)",
                            textTransform: "uppercase",
                            marginBottom: "12px",
                          }}
                        >
                          Pending
                        </h3>

                        {/* Outgoing Friend Requests */}
                        {outgoingRequests.map((request) => (
                          <FriendListItem
                            key={request.id}
                            friend={{
                              id: request.id,
                              userId: request.toUserId,
                              username: request.pendingUser
                                ? request.toEmail?.split("@")[0] || "Unknown"
                                : request.toUsername ||
                                  request.invitedUsername ||
                                  request.invitedEmail ||
                                  "Unknown",
                              email: !request.viaInviteCode
                                ? request.toEmail || request.invitedEmail
                                : undefined,
                              status: "offline",
                              // Don't show lastSeen for privacy - they aren't friends yet
                              addedAt: request.createdAt,
                              shareActivities:
                                (request as any).shareActivities !== false,
                              shareLocation:
                                (request as any).shareLocation === true,
                              shareStatus:
                                (request as any).shareStatus !== false,
                              shareRoutes:
                                (request as any).shareRoutes === true,
                            }}
                            isPending={true}
                            onRemove={() => handleDeclineRequest(request)}
                            onToggleShareActivities={async () => {
                              await friendsService.updateFriendRequestSettings(
                                request.id,
                                {
                                  shareActivities: !(
                                    (request as any).shareActivities !== false
                                  ),
                                },
                              );
                              const requestsData =
                                await friendsService.getFriendRequests();
                              setFriendRequests(requestsData);
                            }}
                            onToggleShareLocation={async () => {
                              await friendsService.updateFriendRequestSettings(
                                request.id,
                                {
                                  shareLocation: !(
                                    (request as any).shareLocation === true
                                  ),
                                },
                              );
                              const requestsData =
                                await friendsService.getFriendRequests();
                              setFriendRequests(requestsData);
                            }}
                            onToggleShareStatus={async () => {
                              await friendsService.updateFriendRequestSettings(
                                request.id,
                                {
                                  shareStatus: !(
                                    (request as any).shareStatus !== false
                                  ),
                                },
                              );
                              const requestsData =
                                await friendsService.getFriendRequests();
                              setFriendRequests(requestsData);
                            }}
                            onToggleShareRoutes={async () => {
                              await friendsService.updateFriendRequestSettings(
                                request.id,
                                {
                                  shareRoutes: !(
                                    (request as any).shareRoutes === true
                                  ),
                                },
                              );
                              const requestsData =
                                await friendsService.getFriendRequests();
                              setFriendRequests(requestsData);
                            }}
                          />
                        ))}
                      </div>
                    )}

                    {/* Actual Friends */}
                    {friends.length > 0 && (
                      <>
                        {outgoingRequests.length > 0 && (
                          <h3
                            style={{
                              fontSize: "14px",
                              color: "var(--text-secondary)",
                              textTransform: "uppercase",
                              marginBottom: "12px",
                            }}
                          >
                            Friends
                          </h3>
                        )}
                        {friends.map((friend) => (
                          <FriendListItem
                            key={friend.id}
                            friend={friend}
                            isPending={false}
                            onRemove={handleRemoveFriend}
                            onToggleShareActivities={
                              handleToggleShareActivities
                            }
                            onToggleShareLocation={handleToggleShareLocation}
                            onToggleShareStatus={handleToggleShareStatus}
                            onToggleShareRoutes={handleToggleShareRoutes}
                            onUpdateMapSettings={handleUpdateMapSettings}
                          />
                        ))}
                      </>
                    )}

                    {/* Separator before incoming requests */}
                    {incomingRequests.length > 0 &&
                      (friends.length > 0 || outgoingRequests.length > 0) && (
                        <hr
                          style={{
                            border: "none",
                            borderTop: "1px solid var(--border)",
                            margin: "20px 0",
                          }}
                        />
                      )}

                    {/* Incoming Requests - Show Last */}
                    {incomingRequests.length > 0 && (
                      <div style={{ marginBottom: "20px" }}>
                        <h3
                          style={{
                            fontSize: "14px",
                            color: "var(--text-secondary)",
                            textTransform: "uppercase",
                            marginBottom: "12px",
                          }}
                        >
                          Incoming Requests
                        </h3>
                        {incomingRequests.map((request) => (
                          <FriendRequestItem
                            key={request.id}
                            request={request}
                            onAccept={handleAcceptRequest}
                            onDecline={handleDeclineRequest}
                          />
                        ))}
                      </div>
                    )}

                    {/* Connect Button at bottom of friends list */}
                    {friends.length > 0 && (
                      <button
                        onClick={() => setActiveTab("invite")}
                        style={{
                          width: "100%",
                          marginTop: "16px",
                          padding: "12px",
                          background: "var(--bg-subtle)",
                          border: "1px solid var(--border)",
                          borderRadius: "6px",
                          color: "var(--text-primary)",
                          cursor: "pointer",
                          fontSize: "14px",
                          fontWeight: "500",
                          transition: "background 0.2s",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background =
                            "var(--bg-primary)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = "var(--bg-subtle)";
                        }}
                      >
                        + Connect with More Friends
                      </button>
                    )}
                  </>
                )}
              </>
            )}

            {/* Connect Tab */}
            {activeTab === "invite" && (
              <div>
                {/* Email Invite */}
                <div style={{ marginBottom: "32px" }}>
                  <h3
                    style={{
                      fontSize: "14px",
                      color: "var(--text-secondary)",
                      textTransform: "uppercase",
                      marginBottom: "12px",
                    }}
                  >
                    Invite by Email
                  </h3>
                  <div style={{ display: "flex", gap: "8px" }}>
                    <input
                      type="email"
                      value={emailInput}
                      onChange={(e) => setEmailInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          handleSendEmailInvite();
                        }
                      }}
                      placeholder="friend@example.com"
                      style={{
                        flex: 1,
                        padding: "12px",
                        background: "var(--bg-subtle)",
                        border: "1px solid var(--border)",
                        borderRadius: "6px",
                        color: "var(--text-primary)",
                        fontSize: "14px",
                      }}
                    />
                    <button
                      onClick={handleSendEmailInvite}
                      disabled={!emailInput.trim()}
                      style={{
                        padding: "12px 24px",
                        background: emailInput.trim()
                          ? "var(--accent)"
                          : "var(--bg-subtle)",
                        border: emailInput.trim()
                          ? "none"
                          : "1px solid var(--border)",
                        borderRadius: "6px",
                        color: emailInput.trim()
                          ? "var(--text-on-accent)"
                          : "var(--text-secondary)",
                        cursor: emailInput.trim() ? "pointer" : "not-allowed",
                        fontSize: "14px",
                        fontWeight: "500",
                        opacity: emailInput.trim() ? 1 : 0.3,
                      }}
                    >
                      Send
                    </button>
                  </div>
                </div>

                {/* Join with Code */}
                <div style={{ marginBottom: "32px" }}>
                  <h3
                    style={{
                      fontSize: "14px",
                      color: "var(--text-secondary)",
                      textTransform: "uppercase",
                      marginBottom: "12px",
                    }}
                  >
                    Connect With Friend Code
                  </h3>
                  <div style={{ display: "flex", gap: "8px" }}>
                    <input
                      type="text"
                      value={codeInput}
                      onChange={(e) => {
                        const value = e.target.value
                          .replace(/\D/g, "")
                          .slice(0, 6);
                        setCodeInput(value);
                      }}
                      placeholder="000000"
                      maxLength={6}
                      style={{
                        flex: 1,
                        padding: "12px",
                        background: "var(--bg-subtle)",
                        border: "1px solid var(--border)",
                        borderRadius: "6px",
                        color: "var(--text-primary)",
                        fontSize: "20px",
                        fontFamily: "monospace",
                        letterSpacing: "0.2em",
                        textAlign: "center",
                      }}
                    />
                    <button
                      onClick={handleJoinWithCode}
                      disabled={codeInput.length !== 6}
                      style={{
                        padding: "12px 24px",
                        background:
                          codeInput.length === 6
                            ? "var(--accent)"
                            : "var(--bg-subtle)",
                        border:
                          codeInput.length === 6
                            ? "none"
                            : "1px solid var(--border)",
                        borderRadius: "6px",
                        color:
                          codeInput.length === 6
                            ? "var(--text-on-accent)"
                            : "var(--text-secondary)",
                        cursor:
                          codeInput.length === 6 ? "pointer" : "not-allowed",
                        fontSize: "14px",
                        fontWeight: "500",
                        opacity: codeInput.length === 6 ? 1 : 0.3,
                      }}
                    >
                      Connect
                    </button>
                  </div>
                </div>

                {/* Invite Code */}
                <div style={{ marginBottom: "32px" }}>
                  <h3
                    style={{
                      fontSize: "14px",
                      color: "var(--text-secondary)",
                      textTransform: "uppercase",
                      marginBottom: "12px",
                    }}
                  >
                    Your Invite Code
                  </h3>

                  {myInviteCode ? (
                    <div
                      style={{
                        padding: "16px",
                        background: "var(--bg-subtle)",
                        borderRadius: "8px",
                      }}
                    >
                      {/* Code Display */}
                      <div>
                        <div
                          style={{
                            fontSize: "32px",
                            fontWeight: "bold",
                            letterSpacing: "0.1em",
                            color: "var(--accent)",
                            fontFamily: "monospace",
                            marginBottom: "8px",
                          }}
                        >
                          {myInviteCode.code}
                        </div>
                        <div
                          style={{
                            fontSize: "12px",
                            color: "var(--text-secondary)",
                          }}
                        >
                          {formatTimeRemaining(myInviteCode.expiresAt)}
                          {myInviteCode.useCount > 0 &&
                            ` • ${myInviteCode.useCount} use${myInviteCode.useCount !== 1 ? "s" : ""}`}
                        </div>
                      </div>

                      {/* Usage List */}
                      {myInviteCode.usedBy.length > 0 && (
                        <div
                          style={{
                            marginTop: "12px",
                            padding: "8px",
                            background: "var(--bg-primary)",
                            borderRadius: "4px",
                          }}
                        >
                          <div
                            style={{
                              fontSize: "11px",
                              color: "var(--text-muted)",
                              marginBottom: "4px",
                              textTransform: "uppercase",
                            }}
                          >
                            Used By:
                          </div>
                          {myInviteCode.usedBy.map((usage, idx) => (
                            <div
                              key={idx}
                              style={{
                                fontSize: "12px",
                                color: "var(--text-primary)",
                                padding: "2px 0",
                              }}
                            >
                              {usage.username} •{" "}
                              {new Date(usage.usedAt).toLocaleDateString()}
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Action Buttons */}
                      <div
                        style={{
                          display: "flex",
                          gap: "8px",
                          marginTop: "12px",
                        }}
                      >
                        <button
                          onClick={handleShareInviteCode}
                          style={{
                            flex: 1,
                            padding: "10px",
                            background: "var(--accent)",
                            border: "none",
                            borderRadius: "6px",
                            color: "var(--text-on-accent)",
                            cursor: "pointer",
                            fontSize: "13px",
                            fontWeight: "500",
                          }}
                        >
                          Share
                        </button>
                        <button
                          onClick={handleGenerateInviteCode}
                          style={{
                            padding: "10px 16px",
                            background: "transparent",
                            border: "1px solid var(--border)",
                            borderRadius: "6px",
                            color: "var(--text-primary)",
                            cursor: "pointer",
                            fontSize: "13px",
                            fontWeight: "500",
                          }}
                        >
                          Refresh Code
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div
                      style={{
                        padding: "24px 20px",
                        textAlign: "center",
                        background: "var(--bg-subtle)",
                        borderRadius: "8px",
                      }}
                    >
                      <div
                        style={{
                          color: "var(--text-secondary)",
                          fontSize: "14px",
                          marginBottom: "12px",
                        }}
                      >
                        Generate a 6-digit code to share with friends (valid for
                        48 hours)
                      </div>
                      <button
                        onClick={handleGenerateInviteCode}
                        style={{
                          padding: "10px 24px",
                          background: "var(--accent)",
                          border: "none",
                          borderRadius: "6px",
                          color: "var(--text-on-accent)",
                          cursor: "pointer",
                          fontSize: "14px",
                          fontWeight: "500",
                        }}
                      >
                        Generate Invite Code
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Profile Tab */}
            {activeTab === "profile" && (
              <div>
                {/* Username */}
                <div style={{ marginBottom: "32px" }}>
                  <h3
                    style={{
                      fontSize: "14px",
                      color: "var(--text-secondary)",
                      textTransform: "uppercase",
                      marginBottom: "12px",
                    }}
                  >
                    Username
                  </h3>
                  <input
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder="Enter username"
                    style={{
                      width: "100%",
                      padding: "12px",
                      background: "var(--bg-subtle)",
                      border: "1px solid var(--border)",
                      borderRadius: "6px",
                      color: "var(--text-primary)",
                      fontSize: "14px",
                    }}
                  />
                </div>

                {/* Preview */}
                <div style={{ marginBottom: "32px" }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "16px",
                      padding: "20px",
                      background: "var(--bg-subtle)",
                      borderRadius: "8px",
                    }}
                  >
                    <div
                      style={{
                        width: "60px",
                        height: "60px",
                        borderRadius: "50%",
                        background: mapColor,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: mapIcon ? "32px" : "24px",
                        fontWeight: "600",
                        color: "white",
                      }}
                    >
                      {mapIcon || getUserInitial(username)}
                    </div>
                    <div>
                      <div
                        style={{
                          fontSize: "16px",
                          fontWeight: "600",
                          color: "var(--text-primary)",
                        }}
                      >
                        {username}
                      </div>
                      <div
                        style={{
                          fontSize: "12px",
                          color: "var(--text-secondary)",
                        }}
                      >
                        How you appear on maps
                      </div>
                    </div>
                  </div>
                </div>

                {/* Map Color */}
                <div style={{ marginBottom: "32px" }}>
                  <h3
                    style={{
                      fontSize: "14px",
                      color: "var(--text-secondary)",
                      textTransform: "uppercase",
                      marginBottom: "12px",
                    }}
                  >
                    Map Color
                  </h3>
                  <div
                    style={{
                      fontSize: "12px",
                      color: "var(--text-muted)",
                      marginBottom: "12px",
                    }}
                  >
                    Background color for your map marker
                  </div>

                  {/* Color presets */}
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fill, minmax(50px, 1fr))",
                      gap: "8px",
                      marginBottom: "12px",
                    }}
                  >
                    {[
                      "#6366f1", // Indigo
                      "#8b5cf6", // Purple
                      "#ec4899", // Pink
                      "#f43f5e", // Rose
                      "#f97316", // Orange
                      "#eab308", // Yellow
                      "#22c55e", // Green
                      "#14b8a6", // Teal
                      "#06b6d4", // Cyan
                      "#3b82f6", // Blue
                    ].map((color) => (
                      <button
                        key={color}
                        onClick={() => setMapColor(color)}
                        style={{
                          width: "50px",
                          height: "50px",
                          borderRadius: "8px",
                          background: color,
                          border:
                            mapColor === color ? "3px solid white" : "none",
                          cursor: "pointer",
                          boxShadow:
                            mapColor === color
                              ? "0 0 0 2px var(--accent)"
                              : "none",
                          transition: "all 0.2s",
                        }}
                      />
                    ))}
                  </div>

                  {/* Custom color input */}
                  <div
                    style={{
                      display: "flex",
                      gap: "8px",
                      alignItems: "center",
                    }}
                  >
                    <input
                      type="color"
                      value={mapColor}
                      onChange={(e) => setMapColor(e.target.value)}
                      style={{
                        width: "60px",
                        height: "40px",
                        border: "1px solid var(--border)",
                        borderRadius: "6px",
                        cursor: "pointer",
                      }}
                    />
                    <span
                      style={{
                        fontSize: "14px",
                        color: "var(--text-secondary)",
                      }}
                    >
                      {mapColor.toUpperCase()}
                    </span>
                  </div>
                </div>

                {/* Map Icon */}
                <div style={{ marginBottom: "32px" }}>
                  <h3
                    style={{
                      fontSize: "14px",
                      color: "var(--text-secondary)",
                      textTransform: "uppercase",
                      marginBottom: "12px",
                    }}
                  >
                    Map Icon
                  </h3>
                  <div
                    style={{
                      fontSize: "12px",
                      color: "var(--text-muted)",
                      marginBottom: "12px",
                    }}
                  >
                    Choose how you appear on your friends' maps
                  </div>

                  {/* Icon options */}
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fill, minmax(60px, 1fr))",
                      gap: "8px",
                      marginBottom: "12px",
                    }}
                  >
                    {[
                      "🚴",
                      "🏃",
                      "🏃‍♀️",
                      "🚲",
                      "⚡",
                      "🔥",
                      "🎯",
                      "⭐",
                      "💨",
                      "🚀",
                      "🏆",
                    ].map((icon) => (
                      <button
                        key={icon}
                        onClick={() => setMapIcon(icon)}
                        style={{
                          width: "60px",
                          height: "60px",
                          borderRadius: "8px",
                          background:
                            mapIcon === icon ? mapColor : "var(--bg-subtle)",
                          border:
                            mapIcon === icon
                              ? `2px solid ${mapColor}`
                              : "1px solid var(--border)",
                          cursor: "pointer",
                          fontSize: "32px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          transition: "all 0.2s",
                        }}
                      >
                        {icon}
                      </button>
                    ))}
                    <button
                      onClick={() => setMapIcon("")}
                      style={{
                        width: "60px",
                        height: "60px",
                        borderRadius: "8px",
                        background:
                          mapIcon === "" ? mapColor : "var(--bg-subtle)",
                        border:
                          mapIcon === ""
                            ? `2px solid ${mapColor}`
                            : "1px solid var(--border)",
                        cursor: "pointer",
                        fontSize: "12px",
                        color:
                          mapIcon === "" ? "white" : "var(--text-secondary)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        transition: "all 0.2s",
                      }}
                    >
                      Initials
                    </button>
                  </div>
                </div>

                {/* Auto-save indicator */}
                <div
                  style={{
                    textAlign: "center",
                    fontSize: "12px",
                    color: "var(--text-muted)",
                    fontStyle: "italic",
                  }}
                >
                  Changes save automatically
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Confirmation Dialog */}
        <ConfirmationDialog
          visible={confirmation.visible}
          title={confirmation.title}
          message={confirmation.message}
          confirmText={confirmation.confirmText}
          cancelText={confirmation.cancelText}
          isDangerous={confirmation.isDangerous}
          cancelIsPrimary={confirmation.cancelIsPrimary}
          onConfirm={confirmation.onConfirm}
          onCancel={
            confirmation.onCancel ||
            (() => setConfirmation({ ...confirmation, visible: false }))
          }
        />
      </div>
    </div>
  );
}
